// hooks/useTokenMonitor.js
import { useEffect, useState, useCallback } from "react";
import AuthService from "../services/AuthService";

const ONE_HOUR_MS = 60 * 60 * 1000;

const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;

    // Support JWT base64url
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = atob(base64);

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const useTokenMonitor = (checkInterval = 1000) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [refreshInProgress, setRefreshInProgress] = useState(false);

  // Fonction pour rafraîchir le token
  const refreshToken = useCallback(async () => {
    // Éviter les appels multiples
    if (refreshInProgress) return null;
    
    try {
      setRefreshInProgress(true);
      const result = await AuthService.refreshToken();
      
      if (result && result.token) {     
        localStorage.setItem("ACCESS_TOKEN", result.token);
        return { success: true, token: result.token };
      } else if (result && result.logout) {
        // Si le refresh indique une déconnexion, nettoyer
        localStorage.removeItem("ACCESS_TOKEN");
        window.location.href = "/";
        return { success: false, logout: true };
      }
      return { success: false };
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du token:", error);
      
      // Vérifier si le token actuel est expiré
      const token = localStorage.getItem("ACCESS_TOKEN");
      if (token) {
        const payload = decodeJwtPayload(token);
        if (payload?.exp && payload.exp * 1000 < Date.now()) {
          localStorage.removeItem("ACCESS_TOKEN");
          window.location.href = "/";
          return { success: false, logout: true };
        }
      }
      return { success: false, error };
    } finally {
      setRefreshInProgress(false);
    }
  }, [refreshInProgress]);

  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("ACCESS_TOKEN");

      if (!token) {
        setTimeRemaining(null);
        return;
      }

      const payload = decodeJwtPayload(token);

      const expirationTimeMs = Number.isFinite(payload?.exp)
        ? payload.exp * 1000
        : Date.now() + ONE_HOUR_MS; // 🔹 fallback 1h

      const now = Date.now();
      const remaining = expirationTimeMs - now;

      if (remaining <= 0) {
        setTimeRemaining(0);
        localStorage.removeItem("ACCESS_TOKEN");
        window.location.href = "/";
      } else {
        setTimeRemaining(remaining);
      }
    };

    checkToken();
    const interval = setInterval(checkToken, checkInterval);

    return () => clearInterval(interval);
  }, [checkInterval]);

  const minutes =
    typeof timeRemaining === "number"
      ? Math.floor(timeRemaining / 60000)
      : 0;

  const seconds =
    typeof timeRemaining === "number"
      ? Math.floor((timeRemaining % 60000) / 1000)
      : 0;

  const timeLeftFormatted =
    typeof timeRemaining === "number"
      ? `${minutes}:${seconds.toString().padStart(2, "0")}`
      : null;

  return {
    timeRemaining,
    timeLeftFormatted,
    refreshToken, 
    refreshInProgress,
    showWarning:
      typeof timeRemaining === "number" &&
      timeRemaining <= 10 * 60 * 1000 &&
      timeRemaining > 0,

    showUrgent:
      typeof timeRemaining === "number" &&
      timeRemaining <= 60 * 1000 &&
      timeRemaining > 0,

    showFifteenMinuteAlert:
      typeof timeRemaining === "number" &&
      timeRemaining <= 15 * 60 * 1000 &&
      timeRemaining > 14 * 60 * 1000,
  };
};

export default useTokenMonitor;